/**
 * QRCodeScanner Component
 * Scans QR codes for speurtocht steps
 */

import React, { useState, useEffect } from "react";
import { View, Text, TouchableOpacity, Modal, Vibration } from "react-native";
import { CameraView, useCameraPermissions } from "expo-camera";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

interface QRCodeScannerProps {
  /** Whether the scanner is visible */
  visible: boolean;
  /** Callback when a QR code is scanned */
  onScan: (value: string) => void;
  /** Callback when scanner is closed */
  onClose: () => void;
  /** Optional hint text */
  hint?: string;
}

/**
 * QRCodeScanner component
 * Modal-based QR code scanner using expo-camera
 */
export const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  visible,
  onScan,
  onClose,
  hint,
}) => {
  const colors = useThemeColors();
  const [permission, requestPermission] = useCameraPermissions();
  const [scanned, setScanned] = useState(false);
  const [flashOn, setFlashOn] = useState(false);

  // Reset scanned state when modal opens
  useEffect(() => {
    if (visible) {
      setScanned(false);
    }
  }, [visible]);

  /**
   * Handle barcode scanned
   */
  const handleBarcodeScanned = ({ data }: { data: string }) => {
    if (scanned) return;

    setScanned(true);
    Vibration.vibrate(100);
    onScan(data);
  };

  /**
   * Toggle flash
   */
  const toggleFlash = () => {
    setFlashOn((prev) => !prev);
  };

  // Permission not granted
  if (!permission?.granted) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="fullScreen"
      >
        <View
          className="flex-1 items-center justify-center p-6"
          style={{ backgroundColor: colors.background }}
        >
          <View
            className="w-20 h-20 rounded-full items-center justify-center mb-6"
            style={{ backgroundColor: `${colors.primary}15` }}
          >
            <Ionicons name="camera" size={40} color={colors.primary} />
          </View>

          <Text
            className="text-xl font-bold text-center mb-2"
            style={{ color: colors.text }}
          >
            Camera toegang nodig
          </Text>
          <Text
            className="text-base text-center mb-8"
            style={{ color: colors.textSecondary }}
          >
            Om QR-codes te scannen hebben we toegang tot je camera nodig.
          </Text>

          <TouchableOpacity
            onPress={requestPermission}
            className="px-8 py-4 rounded-xl"
            style={{ backgroundColor: colors.primary }}
            accessibilityRole="button"
            accessibilityLabel="Geef camera toegang"
          >
            <Text className="text-white font-bold text-base">
              Geef Camera Toegang
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={onClose}
            className="px-8 py-4 mt-4"
            accessibilityRole="button"
            accessibilityLabel="Annuleren"
          >
            <Text className="font-medium" style={{ color: colors.textSecondary }}>
              Annuleren
            </Text>
          </TouchableOpacity>
        </View>
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
    >
      <View className="flex-1 bg-black">
        {/* Camera View */}
        <CameraView
          style={{ flex: 1 }}
          facing="back"
          enableTorch={flashOn}
          onBarcodeScanned={scanned ? undefined : handleBarcodeScanned}
          barcodeScannerSettings={{
            barcodeTypes: ["qr"],
          }}
        >
          {/* Overlay */}
          <View className="flex-1">
            {/* Header */}
            <View className="flex-row items-center justify-between px-4 pt-16 pb-4">
              <TouchableOpacity
                onPress={onClose}
                className="w-12 h-12 rounded-full bg-black/50 items-center justify-center"
                accessibilityRole="button"
                accessibilityLabel="Sluiten"
              >
                <Ionicons name="close" size={28} color="#FFFFFF" />
              </TouchableOpacity>

              <Text className="text-white text-lg font-bold">Scan QR-code</Text>

              <TouchableOpacity
                onPress={toggleFlash}
                className="w-12 h-12 rounded-full bg-black/50 items-center justify-center"
                accessibilityRole="button"
                accessibilityLabel={flashOn ? "Flits uit" : "Flits aan"}
              >
                <Ionicons
                  name={flashOn ? "flash" : "flash-off"}
                  size={24}
                  color="#FFFFFF"
                />
              </TouchableOpacity>
            </View>

            {/* Scan Area */}
            <View className="flex-1 items-center justify-center">
              {/* Scanner Frame */}
              <View className="w-64 h-64 relative">
                {/* Corner Borders */}
                <View className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-white rounded-tl-lg" />
                <View className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-white rounded-tr-lg" />
                <View className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-white rounded-bl-lg" />
                <View className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-white rounded-br-lg" />

                {/* Scanning Line Animation (CSS only) */}
                <View className="absolute top-2 left-2 right-2 h-0.5 bg-green-500 opacity-75" />
              </View>

              {/* Hint Text */}
              {hint && (
                <View className="mt-8 mx-8 px-4 py-3 bg-black/50 rounded-xl">
                  <Text className="text-white text-center text-sm">
                    💡 {hint}
                  </Text>
                </View>
              )}
            </View>

            {/* Bottom Instructions */}
            <View className="pb-12 px-8">
              <Text className="text-white/80 text-center text-base">
                Richt je camera op de QR-code
              </Text>

              {scanned && (
                <TouchableOpacity
                  onPress={() => setScanned(false)}
                  className="mt-4 px-6 py-3 bg-white rounded-xl self-center"
                  accessibilityRole="button"
                  accessibilityLabel="Opnieuw scannen"
                >
                  <Text className="text-black font-bold">Opnieuw scannen</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </CameraView>
      </View>
    </Modal>
  );
};

export default QRCodeScanner;

